import React from 'react'

const Services: React.FC = () => {
  const services = [
    {
      title: 'Payment Consultancy',
      description: 'We are experts in our field and know how to get you what you need from our partners. Comprehensive guidance for optimal payment solutions.',
      icon: (
        <svg className="w-12 h-12 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      features: [
        'Expert payment strategy',
        'Provider selection guidance',
        'Cost optimization',
        'Integration planning'
      ]
    },
    {
      title: 'Card Acquiring',
      description: 'Accept card payments from all major card providers from countries all around the world with seamless integration.',
      icon: (
        <svg className="w-12 h-12 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      features: [
        'Visa & Mastercard support',
        'Global card acceptance',
        'Secure processing',
        'Real-time transactions'
      ]
    },
    {
      title: 'Mobile Payments',
      description: 'We have mobile payment methods suitable for all industries including forex, online gaming, and gambling projects.',
      icon: (
        <svg className="w-12 h-12 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      features: [
        'Apple Pay & Google Pay',
        'Industry-specific solutions',
        'High-risk merchant support',
        'Mobile-optimized checkout'
      ]
    },
    {
      title: 'Local Payment Methods',
      description: 'Accept deposits locally via ATM, QR code and bank deposits all over the world for maximum customer convenience.',
      icon: (
        <svg className="w-12 h-12 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      features: [
        'ATM deposits',
        'QR code payments',
        'Bank transfers',
        'Regional payment methods'
      ]
    }
  ]

  return (
    <section id="services" className="py-20 bg-brand-gray-dark relative overflow-hidden matrix-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            What We <span className="gradient-text">Do</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            At Universal Pays, we have access to 100s of payment methods instantly. This means that we can quickly set you up in the right way and with the right providers to suit your needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="group glass-card-intense p-8 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer card-3d"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="service-icon mb-4 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              
              <h3 className="text-xl font-bold mb-2 group-hover:text-brand-blue transition-colors duration-300">
                {service.title}
              </h3>
              
              <p className="text-gray-300 mb-4 group-hover:text-gray-200 transition-colors duration-300">
                {service.description}
              </p>

              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-gray-400">
                    <svg className="w-4 h-4 text-brand-blue mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Service Process */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Our <span className="gradient-text">Process</span></h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              A systematic approach to ensure the best possible outcome for your case
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Assessment', description: 'Initial evaluation of your situation and requirements' },
              { step: '02', title: 'Analysis', description: 'Detailed investigation using advanced tools and techniques' },
              { step: '03', title: 'Recovery', description: 'Implementation of recovery solutions and strategies' },
              { step: '04', title: 'Verification', description: 'Confirmation of successful recovery and security measures' }
            ].map((process, index) => (
              <div 
                key={process.step}
                className="text-center glass-card p-6 rounded-xl hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${(index + 6) * 100}ms` }}
              >
                <div className="text-3xl font-bold gradient-text mb-4">{process.step}</div>
                <h4 className="text-xl font-bold mb-2">{process.title}</h4>
                <p className="text-gray-300 text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Services
